/**
 * CORE-1693 — окно «Репост в свою ленту»: предпросмотр оригинала и
 * необязательный собственный текст. Отправить можно и без слов.
 *
 * Одно окно на веб и устройство: RN-примитивы, цвета темы, строки из
 * словаря `common:repost`.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useTheme } from '../../context/ThemeContext';
import { useI18n } from '../../i18n';
import { getFileUrl } from '../../services/api';
import { friendlyApiError } from '../../services/errorMessages';
import { classifyRepostError, publishRepost, repostIntentFor, type RepostIntent, type RepostPreview } from '../../services/repost';
import { showFlashNotice } from '../../utils/flashNotice';
import { Avatar } from '../Avatar';
import { fontWeights } from '../../styles/typographyTokens';
import type { MasterPost, RepostTarget } from '../../types/auth';

import { ACCENT_TEXT, ON_ACCENT_TEXT } from '../../styles/accentText';
const MAX_LENGTH = 10000;

interface Props {
  request: { target: RepostTarget; preview: RepostPreview } | null;
  /** Мастер публикует в ленту мастера; остальные — в свою ленту пользователя. */
  asMaster: boolean;
  onClose: () => void;
  onPublished: (post: MasterPost) => void;
}

export const KIND_LABEL_KEYS = {
  post: 'repost.kindPost',
  announcement: 'repost.kindAnnouncement',
  event: 'repost.kindEvent',
  practice: 'repost.kindPractice',
  recording: 'repost.kindRecording',
  room: 'repost.kindRoom',
} as const;

export const RepostComposerModal: React.FC<Props> = ({ request, asMaster, onClose, onPublished }) => {
  const { colors, isDark } = useTheme();
  const { t } = useI18n('common');
  const { width } = useWindowDimensions();
  const isDesktop = width >= 768;
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<TextInput>(null);
  // CORE-1872 — ключ текущего намерения: живёт между повторами после ошибки и
  // сбрасывается при новом открытии окна или другом оригинале.
  const intentRef = useRef<RepostIntent | null>(null);
  const visible = !!request;

  useEffect(() => {
    if (visible) {
      intentRef.current = null;
      setText('');
      setError(null);
      setBusy(false);
    }
  }, [visible, request?.target.kind, request?.target.id]);

  if (!request) return null;
  const { target, preview } = request;

  const cover = getFileUrl(preview.coverUrl ?? '') || null;
  const kindLabel = t(KIND_LABEL_KEYS[preview.kind]);

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const intent = repostIntentFor(intentRef.current, text);
      intentRef.current = intent;
      const post = await publishRepost({ target, content: text, asMaster, idempotencyKey: intent.key });
      intentRef.current = null;
      showFlashNotice(t('repost.done'));
      onPublished(post);
    } catch (e) {
      const why = classifyRepostError(e);
      if (why === 'not_allowed') setError(t('repost.errNotAllowed'));
      else if (why === 'not_found') setError(t('repost.errNotFound'));
      else setError(friendlyApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const card = (
    <View
      testID="repost-composer"
      style={[
        styles.card,
        isDesktop ? styles.cardDesktop : styles.cardSheet,
        { backgroundColor: colors.surfaceElevated, borderColor: colors.border },
      ]}
    >
      <View style={styles.header}>
        <Text style={[styles.title, { color: colors.textPrimary }]}>{t('repost.title')}</Text>
        <TouchableOpacity
          testID="repost-composer-close"
          accessibilityRole="button"
          accessibilityLabel={t('buttons.cancel')}
          onPress={busy ? undefined : onClose}
          hitSlop={8}
        >
          <Ionicons name="close" size={22} color={colors.textMuted} accessible={false} />
        </TouchableOpacity>
      </View>

      <ScrollView keyboardShouldPersistTaps="handled" style={styles.scroll} contentContainerStyle={styles.scrollInner}>
        <TextInput
          ref={inputRef}
          testID="repost-composer-text"
          value={text}
          onChangeText={setText}
          placeholder={t('repost.placeholder')}
          placeholderTextColor={colors.textMuted}
          multiline
          maxLength={MAX_LENGTH}
          editable={!busy}
          style={[
            styles.input,
            { color: colors.textPrimary, borderColor: colors.border, backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,20,25,0.03)' },
            Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : null,
          ]}
        />

        {/* Предпросмотр оригинала: кто, что и какого вида. */}
        <View testID="repost-composer-preview" style={[styles.preview, { borderColor: colors.border, backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(233,130,69,0.05)' }]}>
          {cover ? (
            <Image source={{ uri: cover }} style={styles.cover} resizeMode="cover" />
          ) : (
            <Avatar uri={getFileUrl(preview.authorPhotoUrl ?? '')} name={preview.authorName} size={44} />
          )}
          <View style={styles.previewText}>
            <Text style={[styles.previewKind, { color: ACCENT_TEXT }]} numberOfLines={1}>
              {kindLabel}{preview.authorName ? ` · ${preview.authorName}` : ''}
            </Text>
            {!!preview.title && (
              <Text style={[styles.previewTitle, { color: colors.textPrimary }]} numberOfLines={2}>{preview.title}</Text>
            )}
            {!!preview.text && (
              <Text style={[styles.previewBody, { color: colors.textMuted }]} numberOfLines={3}>{preview.text}</Text>
            )}
          </View>
        </View>

        {!!error && (
          <Text testID="repost-composer-error" style={[styles.error, { color: colors.error }]}>{error}</Text>
        )}
      </ScrollView>

      <View style={styles.footer}>
        <TouchableOpacity
          testID="repost-composer-cancel"
          accessibilityRole="button"
          style={[styles.btn, { borderColor: colors.border }]}
          onPress={busy ? undefined : onClose}
          activeOpacity={0.8}
        >
          <Text style={[styles.btnText, { color: colors.textPrimary }]}>{t('buttons.cancel')}</Text>
        </TouchableOpacity>
        <TouchableOpacity
          testID="repost-composer-submit"
          accessibilityRole="button"
          style={[styles.btn, styles.btnPrimary, { backgroundColor: colors.solar, borderColor: colors.solar }, busy && { opacity: 0.7 }]}
          onPress={() => { void submit(); }}
          disabled={busy}
          activeOpacity={0.85}
        >
          {busy ? (
            <ActivityIndicator size="small" color={ON_ACCENT_TEXT} />
          ) : (
            <>
              <Ionicons name="repeat-outline" size={18} color={ON_ACCENT_TEXT} accessible={false} />
              <Text style={[styles.btnText, { color: ON_ACCENT_TEXT }]}>{t('repost.publish')}</Text>
            </>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );

  return (
    <Modal visible transparent animationType="fade" onRequestClose={busy ? undefined : onClose}>
      <Pressable style={[styles.backdrop, isDesktop ? styles.backdropCenter : styles.backdropBottom]} onPress={busy ? undefined : onClose}>
        <Pressable onPress={() => {}} style={isDesktop ? styles.cardWrapDesktop : styles.cardWrapSheet}>
          {card}
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: 'rgba(15,20,25,0.55)' },
  backdropCenter: { alignItems: 'center', justifyContent: 'center', padding: 24 },
  backdropBottom: { justifyContent: 'flex-end' },
  cardWrapDesktop: { width: '100%', maxWidth: 520 },
  cardWrapSheet: { width: '100%' },
  card: { borderWidth: 1, overflow: 'hidden' },
  cardDesktop: { borderRadius: 20 },
  cardSheet: { borderTopLeftRadius: 24, borderTopRightRadius: 24, maxHeight: '88%', paddingBottom: 24 },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 20, paddingTop: 18, paddingBottom: 10 },
  title: { fontSize: 18, fontWeight: fontWeights.bold },
  scroll: { flexGrow: 0 },
  scrollInner: { paddingHorizontal: 20, paddingBottom: 8 },
  input: {
    minHeight: 96,
    maxHeight: 220,
    borderWidth: 1,
    borderRadius: 14,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 15,
    lineHeight: 21,
    textAlignVertical: 'top',
  },
  preview: { flexDirection: 'row', gap: 12, marginTop: 12, padding: 12, borderWidth: 1, borderRadius: 14, alignItems: 'center' },
  cover: { width: 64, height: 64, borderRadius: 10 },
  previewText: { flex: 1, minWidth: 0 },
  previewKind: { fontSize: 12, fontWeight: fontWeights.bold, letterSpacing: 0.3, textTransform: 'uppercase' },
  previewTitle: { fontSize: 15, fontWeight: fontWeights.semibold, marginTop: 2 },
  previewBody: { fontSize: 13, lineHeight: 18, marginTop: 2 },
  error: { fontSize: 13, lineHeight: 18, marginTop: 10 },
  footer: { flexDirection: 'row', justifyContent: 'flex-end', gap: 10, paddingHorizontal: 20, paddingTop: 12, paddingBottom: 18 },
  btn: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 16, height: 42, borderRadius: 12, borderWidth: 1 },
  btnPrimary: { minWidth: 150, justifyContent: 'center' },
  btnText: { fontSize: 14, fontWeight: fontWeights.semibold },
});
